/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

import SchoolHolidaysSection from "./SchoolHolidaysSection";

import {
  getHolidayForDate,
  niceHolidayType,
} from "../../utils/schoolHolidayUtils";
import {
  FadeUpStagger,
  MotionCard,
  MotionButton,
} from "../motion/AdminMotion";

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function titleCase(value) {
  const raw = String(value || "");
  if (!raw) return "—";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

const ISSUE_STATUSES = new Set([
  "missing",
  "incomplete",
  "partial",
  "open",
  "reopen_requested",
  "reopened",
]);

function isIssueStatus(status) {
  return ISSUE_STATUSES.has(String(status || "").toLowerCase());
}

function getIssueLabel(row) {
  const status = String(row?.registerStatus || "").toLowerCase();

  if (status === "reopen_requested") {
    return "Teacher requested this submitted register to be reopened.";
  }

  if (status === "reopened") {
    return "Register has been reopened and is waiting for teacher resubmission.";
  }

  if (status === "open" || status === "incomplete" || status === "partial") {
    return "Teacher has started or marked attendance but has not submitted the register.";
  }

  if (status === "missing") {
    return "No completed register has been submitted for this class.";
  }

  return "This register needs admin review.";
}

function getReminderCopy(row) {
  const className = row?.className || "your class";
  const date = row?.date || "today";

  return `Hi ${row?.teacherName && row.teacherName !== "—" ? row.teacherName : "there"}, please can you review and submit the ${className} attendance register for ${date}. It is currently showing as incomplete in PresentPlus. Thank you.`;
}

function getStatusTone(status) {
  const value = String(status || "").toLowerCase();

  if (value === "closed") {
    return {
      color: "#9a3412",
      background: "#fff7ed",
      border: "1px solid #fed7aa",
    };
  }

  if (value === "reopen_requested") {
    return {
      color: "#175cd3",
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
    };
  }

  if (value === "reopened") {
    return {
      color: "#3730a3",
      background: "#eef2ff",
      border: "1px solid #c7d2fe",
    };
  }

  if (value === "open") {
    return {
      color: "#175cd3",
      background: "#dbeafe",
      border: "1px solid #bfdcff",
    };
  }

  if (value === "present" || value === "submitted" || value === "complete") {
    return {
      color: "#027a48",
      background: "#dcfae6",
      border: "1px solid #abefc6",
    };
  }

  if (value === "late" || value === "partial" || value === "incomplete") {
    return {
      color: "#b54708",
      background: "#fef0c7",
      border: "1px solid #fedf89",
    };
  }

  if (value === "missing" || value === "absent") {
    return {
      color: "#b42318",
      background: "#fee4e2",
      border: "1px solid #fecdca",
    };
  }

  return {
    color: "#175cd3",
    background: "#dbeafe",
    border: "1px solid #bfdcff",
  };
}

function statusRank(status) {
  const value = String(status || "").toLowerCase();
  if (value === "reopen_requested") return -1;
  if (value === "reopened") return 0;
  if (value === "missing") return 1;
  if (value === "incomplete" || value === "partial" || value === "open") return 2;
  if (value === "late") return 3;
  if (value === "complete" || value === "submitted") return 4;
  return 5;
}

function getBestStatus(existing, next) {
  if (!existing) return next || "";
  if (!next) return existing;

  const existingRank = statusRank(existing);
  const nextRank = statusRank(next);

  return nextRank < existingRank ? next : existing;
}

function normaliseAttendanceDoc(docSnap) {
  const data = docSnap.data() || {};
  const totals = data.totals || {};

  const total = Number(totals.total ?? data.total ?? 0);
  const marked = Number(totals.marked ?? data.marked ?? total ?? 0);
  const present = Number(totals.present ?? data.present ?? 0);
  const absent = Number(totals.absent ?? data.absent ?? 0);
  const late = Number(totals.late ?? data.late ?? 0);

  const rawStatus = String(data.status || data.attendanceStatus || "").toLowerCase();
  const reopenRequest = data.reopenRequest || null;
  const reopenRequestStatus = String(
    reopenRequest?.status || data.reopenRequestStatus || ""
  ).toLowerCase();

  let registerStatus = "missing";

  if (reopenRequestStatus === "pending") {
    registerStatus = "reopen_requested";
  } else if (rawStatus === "reopened") {
    registerStatus = "reopened";
  } else if (rawStatus === "open") {
    registerStatus = "open";
  } else if (rawStatus === "submitted" && total > 0 && marked >= total) {
    registerStatus = late > 0 ? "late" : "complete";
  } else if (rawStatus === "submitted" && total > 0 && marked < total) {
    registerStatus = "incomplete";
  } else if (rawStatus === "draft" || rawStatus === "in_progress") {
    registerStatus = "incomplete";
  }

  return {
    id: docSnap.id,
    sourceRegisterId: docSnap.id,
    ...data,
    date:
      data.date ||
      data.dateKey ||
      data.dayKey ||
      String(docSnap.id || "").split("_")[0] ||
      "",
    classId:
      data.classId ||
      data.classID ||
      String(docSnap.id || "").split("_").slice(1).join("_") ||
      "",
    className: data.className || data.yearGroup || data.class || "",
    status: data.status || data.attendanceStatus || "",
    registerStatus,
    reopenRequest,
    reopenRequestStatus,
    teacherName:
      data.teacherName ||
      data.markedByName ||
      data.staffName ||
      data.markedBy ||
      "—",
    total,
    marked,
    present,
    absent,
    late,
  };
}

export default function AttendanceControlSection({ schoolId }) {
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth() || {};
  const currentSchoolId = schoolId || "school_001";

  const [attendanceDocs, setAttendanceDocs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolHolidays, setSchoolHolidays] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dateFilter, setDateFilter] = useState(localDateKey());
  const [classFilter, setClassFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [reopenTarget, setReopenTarget] = useState(null);
  const [adminReopenReason, setAdminReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);

  const [reviewTarget, setReviewTarget] = useState(null);
  const [adminReviewNote, setAdminReviewNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [copiedReminder, setCopiedReminder] = useState("");


  
  async function loadData() {
    try {
      setLoading(true);
      setError("");

      const [classesSnap, studentsSnap, holidaysSnap, liveRegistersSnap, attendanceSnap] =
        await Promise.all([
          getDocs(collection(db, "schools", currentSchoolId, "classes")),
          getDocs(collection(db, "schools", currentSchoolId, "students")),
          getDocs(
            query(
              collection(db, "schools", currentSchoolId, "schoolHolidays"),
              orderBy("startDate", "asc")
            )
          ),
          getDocs(collection(db, "schools", currentSchoolId, "liveRegisters")),
          getDocs(collection(db, "schools", currentSchoolId, "attendance")),
        ]);

      const classList = classesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => !item.deletedAt)
        .sort((a, b) =>
          String(a.name || a.className || a.yearGroup || "").localeCompare(
            String(b.name || b.className || b.yearGroup || "")
          )
        );

      const studentList = studentsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => !item.deletedAt);

      const holidayList = holidaysSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => !item.deletedAt);

      const registerList = liveRegistersSnap.docs.map((item) => ({
        ...normaliseAttendanceDoc(item),
        sourceCollection: "liveRegisters",
      }));
      const attendanceList = attendanceSnap.docs.map((item) => ({
        ...normaliseAttendanceDoc(item),
        sourceCollection: "attendance",
      }));

      setAttendanceDocs([...attendanceList, ...registerList]);
      setClasses(classList);
      setStudents(studentList);
      setSchoolHolidays(holidayList);
    } catch (err) {
      console.error("Attendance control load error:", err);
      setError("Failed to load attendance control data.");
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    if (!currentSchoolId) return;
    loadData();
  }, [currentSchoolId]);

  const selectedHoliday = useMemo(
    () => getHolidayForDate(dateFilter, schoolHolidays),
    [dateFilter, schoolHolidays]
  );
  const isClosedDay = !!selectedHoliday;

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [classes]);

  const classOptions = useMemo(() => {
    return classes.map((item) => ({
      id: item.id,
      label: item.name || item.className || item.yearGroup || item.id,
    }));
  }, [classes]);

const registerRows = useMemo(() => {
  const grouped = {};

  attendanceDocs.forEach((item) => {
    const dateKey = String(item.date || "");
    const classId = String(item.classId || "");
    const className =
      item.className ||
      classMap[classId]?.name ||
      classMap[classId]?.className ||
      classMap[classId]?.yearGroup ||
      "Unassigned";

    const groupKey = `${dateKey}__${classId || className}`;

    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        id: groupKey,
        date: dateKey,
        classId: classId || "",
        className,
        status: "",
        registerStatus: "",
        teacherName: item.teacherName || "—",
        total: 0,
        marked: 0,
        present: 0,
        absent: 0,
        late: 0,
        reopenRequest: null,
        reopenRequestStatus: "",
        sourceCollection: item.sourceCollection || "liveRegisters",
        adminReview: item.adminReview || null,
      };
    }

    grouped[groupKey].total += Number(item.total || 0);
    grouped[groupKey].marked += Number(item.marked || 0);
    grouped[groupKey].present += Number(item.present || 0);
    grouped[groupKey].absent += Number(item.absent || 0);
    grouped[groupKey].late += Number(item.late || 0);

    grouped[groupKey].teacherName =
      item.teacherName && item.teacherName !== "—"
        ? item.teacherName
        : grouped[groupKey].teacherName;

    grouped[groupKey].registerStatus = getBestStatus(
      grouped[groupKey].registerStatus,
      item.registerStatus
    );

    if (item.adminReview) {
      grouped[groupKey].adminReview = item.adminReview;
    }

    if (item.reopenRequestStatus === "pending") {
      grouped[groupKey].registerStatus = "reopen_requested";
      grouped[groupKey].reopenRequest = item.reopenRequest;
      grouped[groupKey].reopenRequestStatus = item.reopenRequestStatus;
      grouped[groupKey].sourceRegisterId = item.sourceRegisterId || item.id;
      grouped[groupKey].sourceCollection = item.sourceCollection || "liveRegisters";
    } else if (!grouped[groupKey].sourceRegisterId) {
      grouped[groupKey].sourceRegisterId = item.sourceRegisterId || item.id;
      grouped[groupKey].sourceCollection = item.sourceCollection || "liveRegisters";
    }
  });

  return Object.values(grouped).sort((a, b) => {
    const byDate = String(b.date || "").localeCompare(String(a.date || ""));
    if (byDate !== 0) return byDate;
    return String(a.className || "").localeCompare(String(b.className || ""));
  });
}, [attendanceDocs, classMap]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return registerRows.filter((row) => {
      if (dateFilter && row.date !== dateFilter) return false;
      if (classFilter !== "all" && row.classId !== classFilter) return false;

      if (statusFilter === "issues") {
        if (!isIssueStatus(row.registerStatus)) return false;
      } else if (statusFilter !== "all" && row.registerStatus !== statusFilter) {
        return false;
      }

      if (!q) return true;

      return [
        row.className,
        row.teacherName,
        row.registerStatus,
        row.date,
        row.reopenRequest?.reason,
        row.sourceRegisterId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [registerRows, dateFilter, classFilter, statusFilter, search]);

  const summary = useMemo(() => {
    if (isClosedDay) {
      return {
        total: 0,
        complete: 0,
        late: 0,
        missing: 0,
      };
    }

    let total = 0;
    let complete = 0;
    let late = 0;
    let missing = 0;

    registerRows.forEach((row) => {
      if (dateFilter && row.date !== dateFilter) return;

      total += 1;

      if (row.registerStatus === "complete") complete += 1;
      else if (row.registerStatus === "late") late += 1;
      else missing += 1;
    });

    return { total, complete, late, missing };
  }, [registerRows, dateFilter, isClosedDay]);

  const classInsights = useMemo(() => {
    if (isClosedDay) return [];

    const map = {};

    filteredRows.forEach((row) => {
      const key = row.className || "Unassigned";

      if (!map[key]) {
        map[key] = {
          name: key,
          total: 0,
          complete: 0,
          issues: 0,
        };
      }

      map[key].total += 1;
      if (row.registerStatus === "complete") map[key].complete += 1;
      else map[key].issues += 1;
    });

    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredRows, isClosedDay]);

  function resetFilters() {
    setDateFilter(localDateKey());
    setClassFilter("all");
    setStatusFilter("all");
    setSearch("");
  }

  useEffect(() => {
    const focusDate = searchParams.get("dateKey");
    const focusClass = searchParams.get("classId");
    const focusStatus = searchParams.get("status");
    const focusRegisterId = searchParams.get("registerId");

    if (focusDate) setDateFilter(focusDate);
    if (focusClass) setClassFilter(focusClass);
    if (focusStatus) {
      const normalizedStatus = String(focusStatus).toLowerCase();
      setStatusFilter(
        ["issue", "issues", "review", "needs-review", "needs_review"].includes(
          normalizedStatus
        )
          ? "issues"
          : normalizedStatus
      );
    }
    if (focusRegisterId) setSearch(focusRegisterId);
  }, [searchParams]);


  async function handleCopyReminder(row) {
    const text = getReminderCopy(row);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopiedReminder(row.id);
        setTimeout(() => setCopiedReminder(""), 1800);
        return;
      }
    } catch (err) {
      console.warn("Clipboard copy failed:", err);
    }

    window.prompt("Copy this reminder message:", text);
  }

  async function handleMarkReviewed() {
    if (!reviewTarget || !currentSchoolId) return;

    const note = adminReviewNote.trim();

    try {
      setReviewing(true);
      setError("");

      const registerId =
        reviewTarget.sourceRegisterId ||
        reviewTarget.id?.split("__")?.[0] ||
        reviewTarget.id;

      const sourceCollection =
        reviewTarget.sourceCollection === "attendance"
          ? "attendance"
          : "liveRegisters";

      await setDoc(
        doc(db, "schools", currentSchoolId, sourceCollection, registerId),
        {
          adminReview: {
            status: "reviewed",
            note,
            reviewedBy: user?.uid || "",
            reviewedByName:
              profile?.fullName ||
              profile?.displayName ||
              user?.email ||
              "Admin",
            reviewedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "schools", currentSchoolId, "auditLogs"), {
        action: "attendance_register_issue_reviewed",
        schoolId: currentSchoolId,
        registerId,
        classId: reviewTarget.classId || "",
        className: reviewTarget.className || "",
        dateKey: reviewTarget.date || "",
        actorUid: user?.uid || "",
        actorName:
          profile?.fullName ||
          profile?.displayName ||
          user?.email ||
          "Admin",
        role: "admin",
        note,
        issue: getIssueLabel(reviewTarget),
        createdAt: serverTimestamp(),
        meta: {
          teacherName: reviewTarget.teacherName || "",
          registerStatus: reviewTarget.registerStatus || "",
          sourceCollection,
        },
      });

      setReviewTarget(null);
      setAdminReviewNote("");
      await loadData();
    } catch (err) {
      console.error("Mark reviewed error:", err);
      setError("Could not mark this register issue as reviewed.");
    } finally {
      setReviewing(false);
    }
  }

  async function handleReopenRegister() {
    if (!reopenTarget || !currentSchoolId) return;

    const reason = adminReopenReason.trim();

    if (reason.length < 8) {
      setError("Please enter a clear admin reason before reopening this register.");
      return;
    }

    try {
      setReopening(true);
      setError("");

      const registerId = reopenTarget.sourceRegisterId || reopenTarget.id?.split("__")?.[0] || reopenTarget.id;
      const registerRef = doc(db, "schools", currentSchoolId, "liveRegisters", registerId);

      await setDoc(
        registerRef,
        {
          status: "reopened",
          reopenRequest: {
            ...(reopenTarget.reopenRequest || {}),
            status: "approved",
            approvedBy: user?.uid || "",
            approvedByName:
              profile?.fullName ||
              profile?.displayName ||
              user?.email ||
              "Admin",
            approvedAt: serverTimestamp(),
            adminReason: reason,
          },
          reopenedBy: user?.uid || "",
          reopenedByName:
            profile?.fullName ||
            profile?.displayName ||
            user?.email ||
            "Admin",
          reopenedAt: serverTimestamp(),
          reopenedReason: reason,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "schools", currentSchoolId, "auditLogs"), {
        action: "attendance_register_reopened",
        schoolId: currentSchoolId,
        registerId,
        classId: reopenTarget.classId || "",
        className: reopenTarget.className || "",
        dateKey: reopenTarget.date || "",
        actorUid: user?.uid || "",
        actorName:
          profile?.fullName ||
          profile?.displayName ||
          user?.email ||
          "Admin",
        role: "admin",
        reason,
        createdAt: serverTimestamp(),
        meta: {
          teacherName: reopenTarget.teacherName || "",
          requestReason: reopenTarget.reopenRequest?.reason || "",
        },
      });

      const relatedNotificationsSnap = await getDocs(
        query(
          collection(db, "schools", currentSchoolId, "adminNotifications"),
          where("registerId", "==", registerId)
        )
      );

      await Promise.all(
        relatedNotificationsSnap.docs
          .filter((item) => {
            const data = item.data() || {};
            return (
              data.type === "attendance_reopen_request" &&
              String(data.status || "").toLowerCase() !== "resolved"
            );
          })
          .map((item) =>
            updateDoc(
              doc(db, "schools", currentSchoolId, "adminNotifications", item.id),
              {
                status: "resolved",
                resolvedAt: serverTimestamp(),
                resolvedBy: user?.uid || "",
                resolvedByName:
                  profile?.fullName ||
                  profile?.displayName ||
                  user?.email ||
                  "Admin",
              }
            )
          )
      );

      setReopenTarget(null);
      setAdminReopenReason("");
      await loadData();
    } catch (err) {
      console.error("Reopen register error:", err);
      setError("Could not reopen this register.");
    } finally {
      setReopening(false);
    }
  }

  return (
    <div style={styles.page}>
      <MotionCard style={styles.hero} hover={false}>
        <div style={styles.heroLeft}>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Attendance Control</h2>
          <p style={styles.heroText}>
            Review incomplete registers, reopen requests, school-specific holidays, and daily class attendance status.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Registers today</div>
          <div style={styles.heroBadgeValue}>{formatCount(summary.total)}</div>
        </div>
      </MotionCard>

      {error ? <div style={styles.errorBox}>{error}</div> : null}

      {isClosedDay ? (
        <MotionCard style={styles.closedDayCard} hover={false}>
          <div>
            <p style={styles.closedDayEyebrow}>School closed</p>
            <h3 style={styles.closedDayTitle}>{selectedHoliday.title || "School holiday"}</h3>
            <p style={styles.closedDayText}>
              {niceHolidayType(selectedHoliday.type)} • {selectedHoliday.startDate}
              {selectedHoliday.endDate && selectedHoliday.endDate !== selectedHoliday.startDate
                ? ` to ${selectedHoliday.endDate}`
                : ""}
            </p>
            {selectedHoliday.notes ? (
              <p style={styles.closedDayNote}>{selectedHoliday.notes}</p>
            ) : null}
          </div>

          <span style={styles.closedDayBadge}>Attendance disabled</span>
        </MotionCard>
      ) : null}

      <section style={styles.statGrid}>
        <FadeUpStagger>
          <StatCard label="Registers" value={summary.total} tone="blue" />
          <StatCard label="Complete" value={summary.complete} tone="success" />
          <StatCard label="Late" value={summary.late} tone="warning" />
          <StatCard label="Issues" value={summary.missing} tone="danger" />
        </FadeUpStagger>
      </section>

      <MotionCard style={styles.panel}>
        <div style={styles.panelHead}>
          <div>
            <div style={styles.panelEyebrow}>Attendance controls</div>
            <h3 style={styles.panelTitle}>Register filters</h3>
            <div style={styles.panelText}>
              Filter by date, class, status, or search by teacher and class name.
            </div>
          </div>
        </div>

        <div style={styles.filterGrid}>
          <input
            type="date"
            style={styles.input}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />

          <select
            style={styles.select}
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            disabled={isClosedDay}
          >
            <option value="all">All classes</option>
            {classOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            style={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={isClosedDay}
          >
            <option value="all">All statuses</option>
            <option value="issues">Needs review</option>
            <option value="complete">Complete</option>
            <option value="late">Late</option>
            <option value="missing">Missing / Issues</option>
            <option value="reopen_requested">Reopen requested</option>
            <option value="reopened">Reopened</option>
          </select>

          <MotionButton onClick={resetFilters} style={styles.primaryButton}>
            Reset Filters
          </MotionButton>
        </div>

        <div style={styles.searchRow}>
          <input
            style={styles.searchInput}
            placeholder={
              isClosedDay
                ? "Attendance search disabled because school is closed"
                : "Search by class, teacher, status, or date"
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={isClosedDay}
          />
        </div>
      </MotionCard>

      <section style={styles.mainGrid}>
        <MotionCard style={styles.panel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Register directory</div>
              <h3 style={styles.panelTitle}>Operational list</h3>
            </div>
          </div>

          {loading ? (
            <div style={styles.emptyState}>Loading attendance registers...</div>
          ) : isClosedDay ? (
            <div style={styles.closedEmptyState}>
              <strong>No register required.</strong>
              <span>
                Attendance is blocked for this date because school is closed.
              </span>
            </div>
          ) : filteredRows.length ? (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Class</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Teacher</th>
                    <th style={styles.th}>Breakdown</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const tone = getStatusTone(row.registerStatus);

                    return (
                      <tr key={row.id}>
                        <td style={styles.td}>
                          <div style={styles.rowMain}>{row.className || "—"}</div>
                        </td>

                        <td style={styles.td}>{row.date || "—"}</td>

                        <td style={styles.td}>{row.teacherName || "—"}</td>

                        <td style={styles.td}>
                          <div style={styles.breakdownText}>
                            {formatCount(row.present)} present · {formatCount(row.absent)} absent · {formatCount(row.late)} late
                          </div>
                        </td>

                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, ...tone }}>
                            {row.registerStatus === "reopen_requested"
                              ? "Reopen requested"
                              : titleCase(row.registerStatus)}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.actionGroup}>
                            {row.reopenRequestStatus === "pending" ? (
                              <MotionButton
                                type="button"
                                onClick={() => {
                                  setReopenTarget(row);
                                  setAdminReopenReason("");
                                  setError("");
                                }}
                                style={styles.reopenButton}
                              >
                                Reopen
                              </MotionButton>
                            ) : null}

                            {isIssueStatus(row.registerStatus) ? (
                              <>
                                <MotionButton
                                  type="button"
                                  onClick={() => {
                                    setReviewTarget(row);
                                    setAdminReviewNote("");
                                    setError("");
                                  }}
                                  style={styles.reviewButton}
                                >
                                  Review
                                </MotionButton>

                                <MotionButton
                                  type="button"
                                  onClick={() => handleCopyReminder(row)}
                                  style={styles.secondaryMiniButton}
                                >
                                  {copiedReminder === row.id ? "Copied" : "Copy reminder"}
                                </MotionButton>
                              </>
                            ) : null}

                            {!isIssueStatus(row.registerStatus) &&
                            row.reopenRequestStatus !== "pending" ? (
                              <span style={styles.mutedAction}>Clear</span>
                            ) : null}
                          </div>

                          {row.adminReview?.status === "reviewed" ? (
                            <div style={styles.reviewedNote}>
                              Reviewed by {row.adminReview.reviewedByName || "Admin"}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.emptyState}>
              No attendance registers matched your filters.
            </div>
          )}
        </MotionCard>

        <MotionCard style={styles.sidePanel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Insights</div>
              <h3 style={styles.panelTitle}>Class status</h3>
              <div style={styles.panelText}>
                Quick view of which classes are clear and which still need review.
              </div>
            </div>
          </div>

          <div style={styles.insightList}>
            {isClosedDay ? (
              <div style={styles.closedMiniCard}>
                School closed: no class register insight needed.
              </div>
            ) : classInsights.length ? (
              classInsights.map((group) => (
                <div key={group.name} style={styles.insightCard}>
                  <div style={styles.insightTop}>
                    <div style={styles.insightTitle}>{group.name}</div>
                    <div style={styles.insightTotal}>{formatCount(group.total)}</div>
                  </div>

                  <div style={styles.insightStats}>
                    <div style={styles.insightGood}>
                      Complete: {formatCount(group.complete)}
                    </div>
                    <div style={styles.insightBad}>
                      Issues: {formatCount(group.issues)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.emptyState}>No class insight available yet.</div>
            )}
          </div>
        </MotionCard>
      </section>


      {reopenTarget ? (
        <div style={styles.modalOverlay} onClick={() => setReopenTarget(null)}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.panelEyebrow}>Admin reopen</div>
              <h3 style={styles.modalTitle}>Reopen {reopenTarget.className || "register"}?</h3>
              <p style={styles.modalText}>
                Teacher request:{" "}
                <strong>{reopenTarget.reopenRequest?.reason || "No reason supplied"}</strong>
              </p>

              <textarea
                value={adminReopenReason}
                onChange={(e) => setAdminReopenReason(e.target.value)}
                placeholder="Enter admin reason for reopening this submitted register"
                style={styles.modalTextarea}
              />

              <div style={styles.modalActions}>
                <MotionButton
                  type="button"
                  onClick={() => {
                    setReopenTarget(null);
                    setAdminReopenReason("");
                  }}
                  style={styles.secondaryButton}
                  disabled={reopening}
                >
                  Cancel
                </MotionButton>

                <MotionButton
                  type="button"
                  onClick={handleReopenRegister}
                  style={styles.primaryButton}
                  disabled={reopening}
                >
                  {reopening ? "Reopening..." : "Reopen Register"}
                </MotionButton>
              </div>
            </div>
          </MotionCard>
        </div>
      ) : null}

      {reviewTarget ? (
        <div style={styles.modalOverlay} onClick={() => setReviewTarget(null)}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.panelEyebrow}>Register issue</div>
              <h3 style={styles.modalTitle}>
                Review {reviewTarget.className || "register"}
              </h3>

              <div style={styles.issueSummaryCard}>
                <div style={styles.issueSummaryTitle}>
                  {getIssueLabel(reviewTarget)}
                </div>
                <div style={styles.issueSummaryGrid}>
                  <span>Date: {reviewTarget.date || "—"}</span>
                  <span>Teacher: {reviewTarget.teacherName || "—"}</span>
                  <span>
                    Marked: {formatCount(reviewTarget.marked)} /{" "}
                    {formatCount(reviewTarget.total)}
                  </span>
                  <span>
                    Breakdown: {formatCount(reviewTarget.present)} present ·{" "}
                    {formatCount(reviewTarget.absent)} absent ·{" "}
                    {formatCount(reviewTarget.late)} late
                  </span>
                </div>
              </div>

              <textarea
                value={adminReviewNote}
                onChange={(e) => setAdminReviewNote(e.target.value)}
                placeholder="Optional admin note, for example: Reminder sent to teacher by phone/email."
                style={styles.modalTextarea}
              />

              <div style={styles.modalActions}>
                <MotionButton
                  type="button"
                  onClick={() => handleCopyReminder(reviewTarget)}
                  style={styles.secondaryButton}
                  disabled={reviewing}
                >
                  {copiedReminder === reviewTarget.id
                    ? "Reminder copied"
                    : "Copy reminder"}
                </MotionButton>

                <MotionButton
                  type="button"
                  onClick={handleMarkReviewed}
                  style={styles.primaryButton}
                  disabled={reviewing}
                >
                  {reviewing ? "Saving..." : "Mark Reviewed"}
                </MotionButton>
              </div>

              <button
                type="button"
                onClick={() => {
                  setReviewTarget(null);
                  setAdminReviewNote("");
                }}
                style={styles.closeTextButton}
                disabled={reviewing}
              >
                Close
              </button>
            </div>
          </MotionCard>
        </div>
      ) : null}

      <SchoolHolidaysSection schoolId={currentSchoolId} />
    </div>
  );
}

function StatCard({ label, value, tone = "default" }) {
  const toneMap = {
    default: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.98) 100%)",
      border: "1px solid rgba(219,231,255,0.95)",
      boxShadow: "0 20px 40px rgba(79, 99, 255, 0.08)",
      valueColor: "#112042",
    },
    success: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(238,252,246,0.98) 100%)",
      border: "1px solid rgba(171,239,198,0.9)",
      boxShadow: "0 20px 40px rgba(2,122,72,0.08)",
      valueColor: "#027a48",
    },
    warning: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,247,236,0.98) 100%)",
      border: "1px solid rgba(254,223,137,0.88)",
      boxShadow: "0 20px 40px rgba(181,71,8,0.08)",
      valueColor: "#b54708",
    },
    danger: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,245,248,0.98) 100%)",
      border: "1px solid rgba(254,205,202,0.88)",
      boxShadow: "0 20px 40px rgba(180,35,24,0.08)",
      valueColor: "#b42318",
    },
    blue: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(239,245,255,0.98) 100%)",
      border: "1px solid rgba(191,220,255,0.9)",
      boxShadow: "0 20px 40px rgba(23,92,211,0.08)",
      valueColor: "#175cd3",
    },
  };

  const toneStyle = toneMap[tone] || toneMap.default;

  return (
    <MotionCard style={{ ...styles.statCard, ...toneStyle }}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: toneStyle.valueColor }}>
        {formatCount(value)}
      </div>
    </MotionCard>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
  },
  hero: {
    borderRadius: 28,
    padding: "30px 28px",
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    boxShadow: "0 28px 70px var(--pp-brand-shadow, var(--pp-primary-shadow, rgba(79,70,229,0.20)))",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
  },
  heroLeft: { minWidth: 0 },
  heroEyebrow: {
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.86,
    marginBottom: 10,
  },
  heroTitle: {
    margin: 0,
    fontSize: 42,
    lineHeight: 1.04,
    letterSpacing: "-0.03em",
    fontWeight: 900,
  },
  heroText: {
    margin: "12px 0 0",
    fontSize: 16,
    lineHeight: 1.65,
    opacity: 0.95,
    maxWidth: 760,
  },
  heroBadge: {
    minWidth: 220,
    borderRadius: 24,
    padding: "18px 20px",
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
    boxShadow: "0 16px 36px rgba(18, 28, 80, 0.16)",
  },
  heroBadgeLabel: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.82,
    marginBottom: 8,
    fontWeight: 700,
  },
  heroBadgeValue: {
    fontSize: 34,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },
  closedDayCard: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 24,
    padding: 20,
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    boxShadow: "0 18px 44px rgba(154,52,18,0.08)",
  },
  closedDayEyebrow: {
    margin: 0,
    color: "#9a3412",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  closedDayTitle: {
    margin: "6px 0 0",
    color: "#111827",
    fontSize: 24,
    fontWeight: 950,
  },
  closedDayText: {
    margin: "6px 0 0",
    color: "#9a3412",
    lineHeight: 1.5,
  },
  closedDayNote: {
    margin: "8px 0 0",
    color: "#7c2d12",
    lineHeight: 1.5,
  },
  closedDayBadge: {
    background: "#ea580c",
    color: "#fff",
    borderRadius: 999,
    padding: "9px 14px",
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  closedEmptyState: {
    padding: 22,
    borderRadius: 18,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    display: "grid",
    gap: 6,
  },
  closedMiniCard: {
    padding: 16,
    borderRadius: 18,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    fontWeight: 800,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },
  statCard: {
    borderRadius: 24,
    padding: "22px 22px",
    background: "#fff",
  },
  statLabel: {
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 800,
    color: "#5d6b8a",
    marginBottom: 12,
  },
  statValue: {
    fontSize: 42,
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: "-0.04em",
  },
  panel: {
    background: "#fff",
    padding: 24,
    borderRadius: 28,
    border: "1px solid #e9eefb",
    boxShadow: "0 18px 42px rgba(17, 32, 66, 0.06)",
  },
  sidePanel: {
    background: "#fff",
    padding: 24,
    borderRadius: 28,
    border: "1px solid #e9eefb",
    boxShadow: "0 18px 42px rgba(17, 32, 66, 0.06)",
    minWidth: 0,
  },
  panelHead: { marginBottom: 18 },
  panelEyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 800,
    marginBottom: 8,
  },
  panelTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 900,
    color: "#112042",
  },
  panelText: {
    marginTop: 8,
    color: "#667085",
    fontSize: 15,
    lineHeight: 1.6,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    alignItems: "center",
  },
  searchRow: { marginTop: 12 },
  input: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
  },
  primaryButton: {
    padding: "14px 18px",
    borderRadius: 16,
    border: "none",
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 16px 30px rgba(79, 99, 255, 0.18)",
    fontSize: 15,
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    padding: "14px 18px",
    borderRadius: 16,
    border: "1px solid #dbe4ff",
    background: "#ffffff",
    color: "#175cd3",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(320px, 0.75fr)",
    gap: 18,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 20,
    border: "1px solid #edf2fb",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
  },
  th: {
    textAlign: "left",
    padding: 14,
    borderBottom: "1px solid #edf2fb",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: "#f9fbff",
  },
  td: {
    padding: 14,
    borderBottom: "1px solid #f1f5fb",
    verticalAlign: "top",
    color: "#14213d",
    fontSize: 14,
  },
  rowMain: {
    fontSize: 15,
    fontWeight: 800,
    color: "#15284b",
  },
  breakdownText: {
    color: "#5d6b8a",
    lineHeight: 1.5,
  },
  statusBadge: {
    padding: "8px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  actionGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },

  reopenButton: {
    border: "none",
    borderRadius: 999,
    background: "#0f49bc",
    color: "#ffffff",
    padding: "9px 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  reviewButton: {
    border: "none",
    borderRadius: 999,
    background: "#111827",
    color: "#ffffff",
    padding: "9px 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  secondaryMiniButton: {
    border: "1px solid #dbe4ff",
    borderRadius: 999,
    background: "#ffffff",
    color: "#175cd3",
    padding: "8px 11px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  mutedAction: {
    color: "#027a48",
    fontSize: 12,
    fontWeight: 900,
  },

  reviewedNote: {
    marginTop: 8,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 750,
  },

  issueSummaryCard: {
    marginTop: 16,
    borderRadius: 20,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    padding: 16,
    color: "#7c2d12",
  },

  issueSummaryTitle: {
    fontSize: 15,
    fontWeight: 950,
    marginBottom: 10,
    color: "#9a3412",
  },

  issueSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    fontSize: 13,
    lineHeight: 1.45,
    color: "#7c2d12",
    fontWeight: 750,
  },

  closeTextButton: {
    marginTop: 14,
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontWeight: 900,
    cursor: "pointer",
    width: "100%",
    padding: 10,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.46)",
    backdropFilter: "blur(12px)",
    display: "grid",
    placeItems: "center",
    padding: 24,
  },

  modalCard: {
    width: "min(560px, 100%)",
    borderRadius: 28,
    padding: 28,
    background: "#fff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 34px 100px rgba(15,23,42,0.24)",
  },

  modalTitle: {
    margin: "8px 0 0",
    fontSize: 24,
    fontWeight: 900,
    letterSpacing: "-0.04em",
    color: "#112042",
  },

  modalText: {
    margin: "12px 0 0",
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.65,
  },

  modalTextarea: {
    width: "100%",
    minHeight: 120,
    marginTop: 16,
    border: "1px solid #dbe4ff",
    borderRadius: 18,
    padding: 14,
    resize: "vertical",
    fontSize: 14,
    lineHeight: 1.55,
    outline: "none",
    boxSizing: "border-box",
  },

  modalActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 18,
  },

  insightList: {
    display: "grid",
    gap: 12,
  },
  insightCard: {
    borderRadius: 20,
    border: "1px solid #edf2fb",
    background: "#fbfcff",
    padding: 16,
  },
  insightTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    marginBottom: 10,
  },
  insightTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#15284b",
  },
  insightTotal: {
    fontSize: 20,
    fontWeight: 900,
    color: "#2845d9",
  },
  insightStats: {
    display: "grid",
    gap: 6,
  },
  insightGood: {
    color: "#027a48",
    fontWeight: 700,
    fontSize: 14,
  },
  insightBad: {
    color: "#b42318",
    fontWeight: 700,
    fontSize: 14,
  },
  emptyState: {
    padding: 20,
    borderRadius: 18,
    background: "#fbfcff",
    border: "1px solid #edf2fb",
    color: "#6b7a99",
    fontSize: 15,
  },
  errorBox: {
    padding: 14,
    borderRadius: 16,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontWeight: 700,
  },
};
